// ------------------ IMPORTS -------------------

import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Select } from "@lockerpm/design";
import {
  TagsInputWithSearch,
  Tag,
  TagsInputWithSearchRef,
} from "#src/commonV2/TagsInputWithSearch";

// Child Components
import { TimeFrameFields } from "./TimeFrameFields";

// Types and Enums
import {
  PassportActionType,
  PassportRuleType,
  PassportSourceDestinationHostType,
  PassportSourceDestinationUserType,
  PassportPeriodType,
} from "#src/services/endpoint/passports/enums";

// Utils
import {
  searchPorts,
  searchDestinations,
  searchSources,
} from "../../utils/passportTagConverters";

// Filter items
import {
  passportTypeFilterItems,
  passportActionFilterItems,
} from "../../filterItems";

interface PassportFormFieldsProps {
  workspaceId: string;
  type: PassportRuleType;
  setType: (type: PassportRuleType) => void;
  action: PassportActionType;
  setAction: (action: PassportActionType) => void;
  sources: Tag[];
  setSources: (sources: Tag[] | ((prev: Tag[]) => Tag[])) => void;
  destinations: Tag[];
  setDestinations: (destinations: Tag[]) => void;
  ports: Tag[];
  setPorts: (ports: Tag[]) => void;
  timeFrameEnabled: boolean;
  setTimeFrameEnabled: (enabled: boolean) => void;
  timeFrameType: PassportPeriodType;
  setTimeFrameType: (type: PassportPeriodType) => void;
  timeFrameRepeatEvery: number;
  setTimeFrameRepeatEvery: (repeatEvery: number) => void;
  timeFrameStartTime: number;
  setTimeFrameStartTime: (startTime: number) => void;
  timeFrameEndTime: number;
  setTimeFrameEndTime: (endTime: number) => void;
  timeFrameRepeatOn: number[];
  setTimeFrameRepeatOn: (repeatOn: number[]) => void;
  timeFrameRepeatOnDate: number;
  setTimeFrameRepeatOnDate: (repeatOn: number) => void;
  sourcesInputRef: React.RefObject<TagsInputWithSearchRef>;
  destinationsInputRef: React.RefObject<TagsInputWithSearchRef>;
  portsInputRef: React.RefObject<TagsInputWithSearchRef>;
}

export const PassportFormFields = ({
  workspaceId,
  type,
  setType,
  action,
  setAction,
  sources,
  setSources,
  destinations,
  setDestinations,
  ports,
  setPorts,
  timeFrameEnabled,
  setTimeFrameEnabled,
  timeFrameType,
  setTimeFrameType,
  timeFrameRepeatEvery,
  setTimeFrameRepeatEvery,
  timeFrameStartTime,
  setTimeFrameStartTime,
  timeFrameEndTime,
  setTimeFrameEndTime,
  timeFrameRepeatOn,
  setTimeFrameRepeatOn,
  timeFrameRepeatOnDate,
  setTimeFrameRepeatOnDate,
  sourcesInputRef,
  destinationsInputRef,
  portsInputRef,
}: PassportFormFieldsProps) => {
  const { t } = useTranslation("endpoint", { keyPrefix: "passports" });

  // Wrapped search functions
  const wrappedSearchSources = useCallback(
    (query: string) => searchSources(workspaceId, query, type),
    [workspaceId, type]
  );

  const wrappedSearchDestinations = useCallback(
    (query: string) => searchDestinations(workspaceId, query),
    [workspaceId]
  );

  // Effect for when passport type changes
  useEffect(() => {
    // When type changes, clear the sources as they depend on the type
    const allowedTypes: string[] =
      type === PassportRuleType.HOST_HOST
        ? Object.values(PassportSourceDestinationHostType)
        : Object.values(PassportSourceDestinationUserType);

    setSources((prev: Tag[]) =>
      prev.filter((source: Tag) => {
        return allowedTypes.includes(source.extraData?.type);
      })
    );
  }, [type, setSources]);

  return (
    <>
      {/* Form fields grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Type */}
        <div>
          <p className="text-gray-v2-700 mb-1.5 text-sm">
            {t("modal.addAndEdit.ruleType.label")}
          </p>
          <Select
            className="w-full"
            value={type}
            onChange={(value) => setType(value)}
            options={passportTypeFilterItems.map((item) => ({
              value: item.value,
              label: item.getLabel(),
            }))}
          />
        </div>

        {/* Action */}
        <div>
          <p className="text-gray-v2-700 mb-1.5 text-sm">
            {t("modal.addAndEdit.action.label")}
          </p>
          <Select
            className="w-full"
            value={action}
            onChange={(value) => setAction(value)}
            options={passportActionFilterItems.map((item) => ({
              value: item.value,
              label: item.getLabel(),
            }))}
          />
        </div>

        {/* Sources */}
        <div>
          <p className="text-gray-v2-700 mb-1.5 text-sm">
            {t("modal.addAndEdit.sources.label")}
          </p>
          <TagsInputWithSearch
            ref={sourcesInputRef}
            tags={sources}
            setTags={setSources}
            searchFunction={wrappedSearchSources}
            placeholder={t("modal.addAndEdit.sources.searchPlaceholder")}
            emptyState={
              <div className="text-gray-v2-600 text-center p-2">
                {t("modal.addAndEdit.sources.noItems")}
              </div>
            }
          />
        </div>

        {/* Destinations */}
        <div>
          <p className="text-gray-v2-700 mb-1.5 text-sm">
            {t("modal.addAndEdit.destinations.label") || "Destinations"}
          </p>
          <TagsInputWithSearch
            ref={destinationsInputRef}
            tags={destinations}
            setTags={setDestinations}
            searchFunction={wrappedSearchDestinations}
            placeholder={t("modal.addAndEdit.destinations.searchPlaceholder")}
            emptyState={
              <div className="text-gray-v2-600 text-center p-2">
                {t("modal.addAndEdit.destinations.noItems")}
              </div>
            }
          />
        </div>
      </div>

      {/* Ports */}
      <div className="mt-6">
        <p className="text-gray-v2-700 mb-1.5 text-sm">
          {t("modal.addAndEdit.ports.label")}
        </p>
        <TagsInputWithSearch
          ref={portsInputRef}
          tags={ports}
          setTags={setPorts}
          searchFunction={searchPorts}
          placeholder={t("modal.addAndEdit.ports.customPortPlaceholder")}
          emptyState={
            <div className="text-gray-v2-600 text-center p-2">
              {t("modal.addAndEdit.ports.noItems")}
            </div>
          }
        />
      </div>

      {/* Time Frame */}
      <TimeFrameFields
        action={action}
        timeFrameEnabled={timeFrameEnabled}
        setTimeFrameEnabled={setTimeFrameEnabled}
        timeFrameType={timeFrameType}
        setTimeFrameType={setTimeFrameType}
        timeFrameRepeatEvery={timeFrameRepeatEvery}
        setTimeFrameRepeatEvery={setTimeFrameRepeatEvery}
        timeFrameStartTime={timeFrameStartTime}
        setTimeFrameStartTime={setTimeFrameStartTime}
        timeFrameEndTime={timeFrameEndTime}
        setTimeFrameEndTime={setTimeFrameEndTime}
        timeFrameRepeatOn={timeFrameRepeatOn}
        setTimeFrameRepeatOn={setTimeFrameRepeatOn}
        timeFrameRepeatOnDate={timeFrameRepeatOnDate}
        setTimeFrameRepeatOnDate={setTimeFrameRepeatOnDate}
      />
    </>
  );
};
